/**
 * Tax & Shipping Settings Page
 *
 * Configure tax zones and shipping zones with location-based rates
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { COUNTRIES, getCountryName } from '../../lib/countries';

interface Store {
  id: string;
  name: string;
  currency: string;
  tax_enabled?: boolean;
  tax_rate?: number;
}

interface OutletContext {
  store: Store;
}

interface TaxZone {
  id: string;
  name: string;
  countries: string[];
  regions: Record<string, string[]>;
  tax_rate: number;
  tax_name: string;
  is_compound: boolean;
  is_shipping_taxable: boolean;
  priority: number;
  is_active: boolean;
}

interface ShippingMethod {
  id: string;
  zone_id: string;
  name: string;
  description: string | null;
  method_type: 'flat_rate' | 'free_shipping' | 'weight_based' | 'price_based';
  cost: number;
  free_shipping_threshold: number | null;
  min_delivery_days: number | null;
  max_delivery_days: number | null;
  is_active: boolean;
}

interface ShippingZone {
  id: string;
  name: string;
  countries: string[];
  regions: Record<string, string[]>;
  is_active: boolean;
  methods: ShippingMethod[];
}

type Tab = 'tax' | 'shipping';

export default function TaxShippingPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const context = useOutletContext<OutletContext | null>();
  const store = context?.store;

  const [activeTab, setActiveTab] = useState<Tab>('tax');
  const [taxZones, setTaxZones] = useState<TaxZone[]>([]);
  const [shippingZones, setShippingZones] = useState<ShippingZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [showTaxZoneModal, setShowTaxZoneModal] = useState(false);
  const [showShippingZoneModal, setShowShippingZoneModal] = useState(false);
  const [showShippingMethodModal, setShowShippingMethodModal] = useState(false);
  const [editingTaxZone, setEditingTaxZone] = useState<TaxZone | null>(null);
  const [editingShippingZone, setEditingShippingZone] = useState<ShippingZone | null>(null);
  const [editingShippingMethod, setEditingShippingMethod] = useState<{ zoneId: string; method: ShippingMethod | null }>({ zoneId: '', method: null });

  // Form states
  const [taxZoneForm, setTaxZoneForm] = useState({
    name: '',
    countries: [] as string[],
    tax_rate: 0,
    tax_name: 'Tax',
    is_shipping_taxable: true,
    is_active: true,
  });

  const [shippingZoneForm, setShippingZoneForm] = useState({
    name: '',
    countries: [] as string[],
    is_active: true,
  });

  const [shippingMethodForm, setShippingMethodForm] = useState({
    name: '',
    description: '',
    method_type: 'flat_rate' as ShippingMethod['method_type'],
    cost: 0,
    free_shipping_threshold: null as number | null,
    min_delivery_days: null as number | null,
    max_delivery_days: null as number | null,
    is_active: true,
  });

  const loadData = useCallback(async () => {
    const id = store?.id || storeId;
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [taxResponse, shippingResponse] = await Promise.all([
        api.get(`/api/ecommerce/stores/${id}/tax-zones`),
        api.get(`/api/ecommerce/stores/${id}/shipping-zones`),
      ]);

      const taxData = await taxResponse.json() as { tax_zones: TaxZone[] };
      const shippingData = await shippingResponse.json() as { shipping_zones: ShippingZone[] };

      setTaxZones(taxData.tax_zones || []);
      setShippingZones(shippingData.shipping_zones || []);
    } catch {
      setError('Failed to load tax and shipping settings');
    } finally {
      setLoading(false);
    }
  }, [store?.id, storeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showSuccessMessage = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  // Tax Zone handlers
  const handleSaveTaxZone = async () => {
    const id = store?.id || storeId;
    if (!id) return;

    if (!taxZoneForm.name.trim()) {
      setError('Zone name is required');
      return;
    }
    if (taxZoneForm.countries.length === 0) {
      setError('At least one country is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingTaxZone) {
        await api.put(`/api/ecommerce/stores/${id}/tax-zones/${editingTaxZone.id}`, taxZoneForm);
      } else {
        await api.post(`/api/ecommerce/stores/${id}/tax-zones`, taxZoneForm);
      }

      await loadData();
      setShowTaxZoneModal(false);
      setEditingTaxZone(null);
      resetTaxZoneForm();
      showSuccessMessage(editingTaxZone ? 'Tax zone updated' : 'Tax zone created');
    } catch {
      setError('Failed to save tax zone');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTaxZone = async (zoneId: string) => {
    const id = store?.id || storeId;
    if (!id) return;

    if (!confirm('Are you sure you want to delete this tax zone?')) return;

    try {
      setSaving(true);
      await api.delete(`/api/ecommerce/stores/${id}/tax-zones/${zoneId}`);
      await loadData();
      showSuccessMessage('Tax zone deleted');
    } catch {
      setError('Failed to delete tax zone');
    } finally {
      setSaving(false);
    }
  };

  const resetTaxZoneForm = () => {
    setTaxZoneForm({
      name: '',
      countries: [],
      tax_rate: 0,
      tax_name: 'Tax',
      is_shipping_taxable: true,
      is_active: true,
    });
  };

  const openEditTaxZone = (zone: TaxZone) => {
    setEditingTaxZone(zone);
    setTaxZoneForm({
      name: zone.name,
      countries: zone.countries,
      tax_rate: zone.tax_rate,
      tax_name: zone.tax_name,
      is_shipping_taxable: zone.is_shipping_taxable,
      is_active: zone.is_active,
    });
    setShowTaxZoneModal(true);
  };

  // Shipping Zone handlers
  const handleSaveShippingZone = async () => {
    const id = store?.id || storeId;
    if (!id) return;

    if (!shippingZoneForm.name.trim()) {
      setError('Zone name is required');
      return;
    }
    if (shippingZoneForm.countries.length === 0) {
      setError('At least one country is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingShippingZone) {
        await api.put(`/api/ecommerce/stores/${id}/shipping-zones/${editingShippingZone.id}`, shippingZoneForm);
      } else {
        await api.post(`/api/ecommerce/stores/${id}/shipping-zones`, shippingZoneForm);
      }

      await loadData();
      setShowShippingZoneModal(false);
      setEditingShippingZone(null);
      resetShippingZoneForm();
      showSuccessMessage(editingShippingZone ? 'Shipping zone updated' : 'Shipping zone created');
    } catch {
      setError('Failed to save shipping zone');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShippingZone = async (zoneId: string) => {
    const id = store?.id || storeId;
    if (!id) return;

    if (!confirm('Are you sure you want to delete this shipping zone and all its methods?')) return;

    try {
      setSaving(true);
      await api.delete(`/api/ecommerce/stores/${id}/shipping-zones/${zoneId}`);
      await loadData();
      showSuccessMessage('Shipping zone deleted');
    } catch {
      setError('Failed to delete shipping zone');
    } finally {
      setSaving(false);
    }
  };

  const resetShippingZoneForm = () => {
    setShippingZoneForm({
      name: '',
      countries: [],
      is_active: true,
    });
  };

  const openEditShippingZone = (zone: ShippingZone) => {
    setEditingShippingZone(zone);
    setShippingZoneForm({
      name: zone.name,
      countries: zone.countries,
      is_active: zone.is_active,
    });
    setShowShippingZoneModal(true);
  };

  // Shipping Method handlers
  const handleSaveShippingMethod = async () => {
    const id = store?.id || storeId;
    if (!id || !editingShippingMethod.zoneId) return;

    if (!shippingMethodForm.name.trim()) {
      setError('Method name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        ...shippingMethodForm,
        cost: Math.round(shippingMethodForm.cost * 100), // Convert to cents
        free_shipping_threshold: shippingMethodForm.free_shipping_threshold
          ? Math.round(shippingMethodForm.free_shipping_threshold * 100)
          : null,
      };

      if (editingShippingMethod.method) {
        await api.put(
          `/api/ecommerce/stores/${id}/shipping-zones/${editingShippingMethod.zoneId}/methods/${editingShippingMethod.method.id}`,
          payload
        );
      } else {
        await api.post(
          `/api/ecommerce/stores/${id}/shipping-zones/${editingShippingMethod.zoneId}/methods`,
          payload
        );
      }

      await loadData();
      setShowShippingMethodModal(false);
      setEditingShippingMethod({ zoneId: '', method: null });
      resetShippingMethodForm();
      showSuccessMessage(editingShippingMethod.method ? 'Shipping method updated' : 'Shipping method created');
    } catch {
      setError('Failed to save shipping method');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShippingMethod = async (zoneId: string, methodId: string) => {
    const id = store?.id || storeId;
    if (!id) return;

    if (!confirm('Are you sure you want to delete this shipping method?')) return;

    try {
      setSaving(true);
      await api.delete(`/api/ecommerce/stores/${id}/shipping-zones/${zoneId}/methods/${methodId}`);
      await loadData();
      showSuccessMessage('Shipping method deleted');
    } catch {
      setError('Failed to delete shipping method');
    } finally {
      setSaving(false);
    }
  };

  const resetShippingMethodForm = () => {
    setShippingMethodForm({
      name: '',
      description: '',
      method_type: 'flat_rate',
      cost: 0,
      free_shipping_threshold: null,
      min_delivery_days: null,
      max_delivery_days: null,
      is_active: true,
    });
  };

  const openAddShippingMethod = (zoneId: string) => {
    setEditingShippingMethod({ zoneId, method: null });
    resetShippingMethodForm();
    setShowShippingMethodModal(true);
  };

  const openEditShippingMethod = (zoneId: string, method: ShippingMethod) => {
    setEditingShippingMethod({ zoneId, method });
    setShippingMethodForm({
      name: method.name,
      description: method.description || '',
      method_type: method.method_type,
      cost: method.cost / 100, // Convert from cents
      free_shipping_threshold: method.free_shipping_threshold ? method.free_shipping_threshold / 100 : null,
      min_delivery_days: method.min_delivery_days,
      max_delivery_days: method.max_delivery_days,
      is_active: method.is_active,
    });
    setShowShippingMethodModal(true);
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: store?.currency || 'USD',
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tax & Shipping</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Configure tax rates and shipping costs based on buyer location
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('tax')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'tax'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Tax Zones
        </button>
        <button
          onClick={() => setActiveTab('shipping')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'shipping'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Shipping Zones
        </button>
      </div>

      {/* Tax Zones Tab */}
      {activeTab === 'tax' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set up tax rates for different countries and regions
            </p>
            <button
              onClick={() => {
                resetTaxZoneForm();
                setEditingTaxZone(null);
                setShowTaxZoneModal(true);
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Tax Zone
            </button>
          </div>

          {taxZones.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No tax zones configured</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Add tax zones to charge different tax rates based on customer location
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {taxZones.map((zone) => (
                <div
                  key={zone.id}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-gray-900 dark:text-white">{zone.name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          zone.is_active
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {zone.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {zone.countries.map(c => getCountryName(c)).join(', ')}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {zone.tax_rate}%
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{zone.tax_name}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditTaxZone(zone)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteTaxZone(zone.id)}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shipping Zones Tab */}
      {activeTab === 'shipping' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set up shipping zones and methods for different locations
            </p>
            <button
              onClick={() => {
                resetShippingZoneForm();
                setEditingShippingZone(null);
                setShowShippingZoneModal(true);
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Shipping Zone
            </button>
          </div>

          {shippingZones.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No shipping zones configured</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Add shipping zones to define shipping rates for different regions
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {shippingZones.map((zone) => (
                <div
                  key={zone.id}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-medium text-gray-900 dark:text-white">{zone.name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            zone.is_active
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {zone.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {zone.countries.map(c => getCountryName(c)).join(', ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openAddShippingMethod(zone.id)}
                          className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          Add Method
                        </button>
                        <button
                          onClick={() => openEditShippingZone(zone)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteShippingZone(zone.id)}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Shipping Methods */}
                  {zone.methods.length > 0 && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {zone.methods.map((method) => (
                        <div key={method.id} className="px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white text-sm">{method.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {method.method_type === 'free_shipping' ? 'Free' : formatCurrency(method.cost)}
                                {method.min_delivery_days && method.max_delivery_days && (
                                  <span className="ml-2">({method.min_delivery_days}-{method.max_delivery_days} days)</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditShippingMethod(zone.id, method)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteShippingMethod(zone.id, method.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {zone.methods.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No shipping methods. Add a method to enable shipping to this zone.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tax Zone Modal */}
      {showTaxZoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {editingTaxZone ? 'Edit Tax Zone' : 'Add Tax Zone'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Zone Name
                  </label>
                  <input
                    type="text"
                    value={taxZoneForm.name}
                    onChange={(e) => setTaxZoneForm({ ...taxZoneForm, name: e.target.value })}
                    placeholder="e.g., United States, Europe"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Countries
                  </label>
                  <select
                    multiple
                    value={taxZoneForm.countries}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setTaxZoneForm({ ...taxZoneForm, countries: selected });
                    }}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 h-40"
                  >
                    {COUNTRIES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Hold Ctrl/Cmd to select multiple countries
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tax Rate (%)
                    </label>
                    <input
                      type="number"
                      value={taxZoneForm.tax_rate}
                      onChange={(e) => setTaxZoneForm({ ...taxZoneForm, tax_rate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tax Name
                    </label>
                    <input
                      type="text"
                      value={taxZoneForm.tax_name}
                      onChange={(e) => setTaxZoneForm({ ...taxZoneForm, tax_name: e.target.value })}
                      placeholder="e.g., VAT, GST, Sales Tax"
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={taxZoneForm.is_shipping_taxable}
                      onChange={(e) => setTaxZoneForm({ ...taxZoneForm, is_shipping_taxable: e.target.checked })}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Tax shipping</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={taxZoneForm.is_active}
                      onChange={(e) => setTaxZoneForm({ ...taxZoneForm, is_active: e.target.checked })}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowTaxZoneModal(false);
                    setEditingTaxZone(null);
                    resetTaxZoneForm();
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTaxZone}
                  disabled={saving}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingTaxZone ? 'Update Zone' : 'Create Zone'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Zone Modal */}
      {showShippingZoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {editingShippingZone ? 'Edit Shipping Zone' : 'Add Shipping Zone'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Zone Name
                  </label>
                  <input
                    type="text"
                    value={shippingZoneForm.name}
                    onChange={(e) => setShippingZoneForm({ ...shippingZoneForm, name: e.target.value })}
                    placeholder="e.g., Domestic, International"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Countries
                  </label>
                  <select
                    multiple
                    value={shippingZoneForm.countries}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setShippingZoneForm({ ...shippingZoneForm, countries: selected });
                    }}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 h-40"
                  >
                    {COUNTRIES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Hold Ctrl/Cmd to select multiple countries
                  </p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shippingZoneForm.is_active}
                    onChange={(e) => setShippingZoneForm({ ...shippingZoneForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowShippingZoneModal(false);
                    setEditingShippingZone(null);
                    resetShippingZoneForm();
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveShippingZone}
                  disabled={saving}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingShippingZone ? 'Update Zone' : 'Create Zone'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Method Modal */}
      {showShippingMethodModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {editingShippingMethod.method ? 'Edit Shipping Method' : 'Add Shipping Method'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Method Name
                  </label>
                  <input
                    type="text"
                    value={shippingMethodForm.name}
                    onChange={(e) => setShippingMethodForm({ ...shippingMethodForm, name: e.target.value })}
                    placeholder="e.g., Standard Shipping, Express"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Method Type
                  </label>
                  <select
                    value={shippingMethodForm.method_type}
                    onChange={(e) => setShippingMethodForm({ ...shippingMethodForm, method_type: e.target.value as ShippingMethod['method_type'] })}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="flat_rate">Flat Rate</option>
                    <option value="free_shipping">Free Shipping</option>
                  </select>
                </div>

                {shippingMethodForm.method_type === 'flat_rate' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cost ({store?.currency || 'USD'})
                      </label>
                      <input
                        type="number"
                        value={shippingMethodForm.cost}
                        onChange={(e) => setShippingMethodForm({ ...shippingMethodForm, cost: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Free Shipping Threshold (optional)
                      </label>
                      <input
                        type="number"
                        value={shippingMethodForm.free_shipping_threshold || ''}
                        onChange={(e) => setShippingMethodForm({
                          ...shippingMethodForm,
                          free_shipping_threshold: e.target.value ? parseFloat(e.target.value) : null
                        })}
                        min="0"
                        step="0.01"
                        placeholder="e.g., 100 for free shipping over $100"
                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Min Delivery Days
                    </label>
                    <input
                      type="number"
                      value={shippingMethodForm.min_delivery_days || ''}
                      onChange={(e) => setShippingMethodForm({
                        ...shippingMethodForm,
                        min_delivery_days: e.target.value ? parseInt(e.target.value) : null
                      })}
                      min="0"
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max Delivery Days
                    </label>
                    <input
                      type="number"
                      value={shippingMethodForm.max_delivery_days || ''}
                      onChange={(e) => setShippingMethodForm({
                        ...shippingMethodForm,
                        max_delivery_days: e.target.value ? parseInt(e.target.value) : null
                      })}
                      min="0"
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shippingMethodForm.is_active}
                    onChange={(e) => setShippingMethodForm({ ...shippingMethodForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowShippingMethodModal(false);
                    setEditingShippingMethod({ zoneId: '', method: null });
                    resetShippingMethodForm();
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveShippingMethod}
                  disabled={saving}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingShippingMethod.method ? 'Update Method' : 'Create Method'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
